import { Routes } from "@angular/router";
import {
    anonymousOnlyGuard,
    authenticatedGuard,
    roleGuard,
    authenticatedMatchGuard,
    roleMatchGuard,
} from "./features/authentication/guards/auth.guards";
import {
    billingRoleGuard,
    paidFeatureGuard,
    companyApplicationAccessGuard,
    companyCapacityGuard,
    planExpiredGuard,
} from "./features/billing/guards/company-entitlement.guards";
import { usersAccessGuard } from "./features/users/data-access/users-access.guard";
import { reportsAccessGuard } from "./features/reports/data-access/reports-access.guard";
import { azureLinkingAccessGuard } from "./features/settings/data-access/azure-linking-access.guard";
import {
    anonymousConferenceRoomRedirect,
    conferenceRoomRedirect,
} from "./features/multiconference/routing/conference-room.redirect";
import { groupsAccessGuard } from "./features/groups/data-access/groups-access.guard";
import { clientOwnerAccessGuard } from "./features/settings/data-access/client-owner-access.guard";
import { nedapAccessGuard } from "./features/nedap/data-access/nedap-access.guard";

export const appRoutes: Routes = [
    { path: "", pathMatch: "full", redirectTo: "login" },
    {
        path: "login",
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
            import("./features/authentication/pages/login/login.page").then(
                (m) => m.LoginPage
            ),
    },
    { path: "qr-login", pathMatch: "full", redirectTo: "/dashboard/qr-login" },
    { path: "glasslogin", pathMatch: "full", redirectTo: "/login?mode=qr" },
    {
        path: "azure/short_value/:token",
        data: { azureQrCallback: true },
        loadComponent: () =>
            import("./features/authentication/pages/azure-callback/azure-callback.page").then(m => m.AzureCallbackPage),
    },
    {
        path: "forgot-password",
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
            import("./features/authentication/pages/forgot-password/forgot-password.page").then(
                (m) => m.ForgotPasswordPage
            ),
    },
    {
        path: "register",
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
            import("./features/authentication/pages/register/register.page").then(
                (m) => m.RegisterPage
            ),
    },
    {
        path: "register2",
        canActivate: [anonymousOnlyGuard],
        loadComponent: () =>
            import("./features/authentication/pages/register-account/register-account.page").then(
                (m) => m.RegisterAccountPage
            ),
    },
    {
        path: "registration2",
        pathMatch: "full",
        redirectTo: "register2",
    },
    {
        path: "register_existing_mail",
        loadComponent: () =>
            import("./features/authentication/pages/register-existing/register-existing.page").then(
                (m) => m.RegisterExistingPage
            ),
    },
    {
        path: "azure/client_value/:token",
        loadComponent: () =>
            import("./features/authentication/pages/azure-registration-callback/azure-registration-callback.page").then(
                (m) => m.AzureRegistrationCallbackPage
            ),
    },
    {
        path: "azuread_accept_token",
        data: { azureAccessTokenCallback: true },
        loadComponent: () =>
            import("./features/authentication/pages/azure-callback/azure-callback.page").then(
                (m) => m.AzureCallbackPage
            ),
    },
    {
        path: "azure/value/:token",
        loadComponent: () =>
            import("./features/authentication/pages/azure-callback/azure-callback.page").then(
                (m) => m.AzureCallbackPage
            ),
    },
    {
        path: "azure/failed/:type",
        loadComponent: () =>
            import("./features/authentication/pages/azure-error/azure-error.page").then(
                (m) => m.AzureErrorPage
            ),
    },
    {
        path: "guestroom/:roomId/:clientId",
        canMatch: [authenticatedMatchGuard, roleMatchGuard("user")],
        canActivate: [authenticatedGuard, roleGuard("user")],
        data: { navigationProfile: "dashboard" },
        loadComponent: () =>
            import("./layout/authenticated-layout/authenticated-shell.component").then(
                (m) => m.AuthenticatedShellComponent
            ),
        children: [
            {
                path: "",
                loadComponent: () =>
                    import("./features/guest-calls/pages/guest-room/guest-room.page").then(
                        (m) => m.GuestRoomPage
                    ),
            },
        ],
    },
    {
        path: "guestroom/:roomId/:clientId",
        loadComponent: () =>
            import("./features/guest-calls/pages/guest-room/guest-room.page").then(
                (m) => m.GuestRoomPage
            ),
    },
    {
        path: "multi",
        loadChildren: () =>
            import("./features/multiconference/guest-conference.routes").then(
                (m) => m.GUEST_CONFERENCE_ROUTES
            ),
    },
    {
        path: "dashboard/multi",
        loadChildren: () =>
            import("./features/multiconference/guest-conference.routes").then(
                (m) => m.GUEST_CONFERENCE_ROUTES
            ),
    },
    {
        path: "dashboard/multiconference/room/:roomId",
        canMatch: [anonymousConferenceRoomRedirect],
        loadComponent: () =>
            import("./features/multiconference/pages/guest-conference/guest-conference.page").then(
                (m) => m.GuestConferencePage
            ),
    },
    {
        path: "conference/:roomId",
        redirectTo: conferenceRoomRedirect,
    },
    {
        path: "",
        canActivate: [authenticatedGuard, roleGuard("user")],
        canActivateChild: [companyCapacityGuard],
        data: { navigationProfile: "dashboard" },
        loadComponent: () =>
            import("./layout/authenticated-layout/authenticated-shell.component").then(
                (m) => m.AuthenticatedShellComponent
            ),
        children: [
            {
                path: "shop",
                canActivate: [billingRoleGuard],
                loadComponent: () =>
                    import("./features/billing/pages/shop/shop.page").then(
                        (m) => m.ShopPage
                    ),
            },
            {
                path: "subscriptions",
                pathMatch: "full",
                redirectTo: "plan-and-billing",
            },
            {
                path: "plan-and-billing",
                canActivate: [billingRoleGuard],
                loadComponent: () =>
                    import("./features/billing/pages/plan-and-billing/plan-and-billing.page").then(
                        (m) => m.PlanAndBillingPage
                    ),
            },
            {
                path: "settings/network",
                pathMatch: "full",
                redirectTo: "/dashboard/settings/network",
            },
            {
                path: "settings/invoices",
                canActivate: [billingRoleGuard],
                loadComponent: () =>
                    import("./features/billing/pages/invoices/invoices.page").then(m => m.InvoicesPage),
            },
            {
                path: "settings/export",
                canActivate: [clientOwnerAccessGuard],
                loadComponent: () => import("./features/settings/pages/export/export.page").then(m => m.ExportPage),
            },
            {
                path: "plan-expired",
                canActivate: [planExpiredGuard],
                loadComponent: () =>
                    import("./features/billing/pages/plan-expired/plan-expired.page").then(
                        (m) => m.PlanExpiredPage
                    ),
            },
            {
                path: "capacity-required",
                loadComponent: () =>
                    import("./features/billing/pages/capacity-required/capacity-required.page").then(
                        (m) => m.CapacityRequiredPage
                    ),
            },
            {
                path: "gallery/view",
                canActivate: [companyApplicationAccessGuard],
                loadComponent: () =>
                    import("./features/profile/pages/view-file/view-file.page").then(
                        (m) => m.ViewFilePage
                    ),
            },
            {
                path: "gallery/bundle/:bundleId",
                canActivate: [companyApplicationAccessGuard],
                loadComponent: () =>
                    import("./features/profile/pages/bundle-viewer/bundle-viewer.page").then(
                        (m) => m.BundleViewerPage
                    ),
            },
            {
                path: "gallery/:id",
                canActivate: [companyApplicationAccessGuard],
                loadComponent: () =>
                    import("./features/profile/pages/view-file/view-file.page").then(
                        (m) => m.ViewFilePage
                    ),
            },
            {
                path: "dashboard",
                canActivate: [companyApplicationAccessGuard],
                children: [
                    {
                        path: "",
                        loadComponent: () =>
                            import("./features/dashboard/pages/dashboard/dashboard.page").then(
                                (m) => m.DashboardPage
                            ),
                    },
                    {
                        path: "qr-login",
                        pathMatch: "full",
                        redirectTo: "/dashboard?qrLogin=1",
                    },
                    {
                        path: "contacts",
                        data: { title: "layout.navigation.contacts.label" },
                        loadComponent: () =>
                            import("./features/contacts/pages/contacts/contacts.page").then(
                                (m) => m.ContactsPage
                            ),
                    },
                    {
                        path: "contacts/:username/owned",
                        data: { title: "profile.tabs.gallery" },
                        loadComponent: () =>
                            import("./features/profile/pages/profile/profile.page").then(
                                (m) => m.ProfilePage
                            ),
                    },
                    {
                        path: "contacts/:username/bundles/:bundleId",
                        data: { title: "profile.bundles.previewTitle" },
                        loadComponent: () =>
                            import("./features/profile/pages/bundle-viewer/bundle-viewer.page").then(
                                (m) => m.BundleViewerPage
                            ),
                    },
                    {
                        path: "contacts/:username/bundles",
                        data: { title: "profile.tabs.shared" },
                        loadComponent: () =>
                            import("./features/profile/pages/profile/profile.page").then(
                                (m) => m.ProfilePage
                            ),
                    },
                    {
                        path: "contacts/:username/activities",
                        data: { title: "profile.tabs.activity" },
                        loadComponent: () =>
                            import("./features/profile/pages/profile/profile.page").then(
                                (m) => m.ProfilePage
                            ),
                    },
                    {
                        path: "gallery/view",
                        data: { title: "profile.viewer.title" },
                        loadComponent: () =>
                            import("./features/profile/pages/view-file/view-file.page").then(
                                (m) => m.ViewFilePage
                            ),
                    },
                    {
                        path: "gallery/:id",
                        data: { title: "profile.viewer.title" },
                        loadComponent: () =>
                            import("./features/profile/pages/view-file/view-file.page").then(
                                (m) => m.ViewFilePage
                            ),
                    },
                    {
                        path: "nedap",
                        canActivate: [nedapAccessGuard],
                        data: { title: "nedap.title" },
                        loadComponent: () =>
                            import("./features/nedap/pages/nedap-upload/nedap-upload.page").then(
                                (m) => m.NedapUploadPage
                            ),
                    },
                    {
                        path: "actions",
                        data: { title: "layout.navigation.actions.label" },
                        loadComponent: () =>
                            import("./features/actions/pages/actions/actions.page").then(
                                (m) => m.ActionsPage
                            ),
                    },
                    {
                        path: "multiconference",
                        data: {
                            title: "layout.navigation.multiconference.label",
                        },
                        loadChildren: () =>
                            import("./features/multiconference/authenticated-conference.routes").then(
                                (m) => m.AUTHENTICATED_CONFERENCE_ROUTES
                            ),
                    },
                    {
                        path: "users",
                        canActivate: [usersAccessGuard],
                        data: { title: "layout.navigation.users.label" },
                        loadComponent: () =>
                            import("./features/users/pages/users/users.page").then(
                                (m) => m.UsersPage
                            ),
                    },
                    {
                        path: "invites",
                        canActivate: [usersAccessGuard],
                        data: { title: "layout.navigation.invites.label" },
                        loadComponent: () =>
                            import("./features/invites/pages/invites/invites.page").then(
                                (m) => m.InvitesPage
                            ),
                    },
                    {
                        path: "groups",
                        canActivate: [groupsAccessGuard],
                        data: { title: "layout.navigation.groups.label" },
                        loadComponent: () =>
                            import("./features/groups/pages/groups/groups.page").then(
                                (m) => m.GroupsPage
                            ),
                    },
                    {
                        path: "features",
                        data: { title: "layout.navigation.features.label" },
                        loadComponent: () =>
                            import("./features/dashboard/pages/placeholder/placeholder.page").then(
                                (m) => m.PlaceholderPage
                            ),
                    },
                    {
                        path: "features/workflows",
                        canActivate: [paidFeatureGuard("WORKFLOWS")],
                        data: { title: "layout.navigation.features.workflows" },
                        loadComponent: () =>
                            import("./features/workflows/pages/workflows/workflows.page").then(
                                (m) => m.WorkflowsPage
                            ),
                    },
                    {
                        path: "features/forms",
                        canActivate: [paidFeatureGuard("FORMS")],
                        data: { title: "layout.navigation.features.forms" },
                        loadComponent: () =>
                            import("./features/forms/pages/forms/forms.page").then(
                                (m) => m.FormsPage
                            ),
                    },
                    {
                        path: "features/reports",
                        canActivate: [reportsAccessGuard],
                        data: { title: "layout.navigation.features.reports" },
                        loadComponent: () =>
                            import("./features/reports/pages/reports/reports.page").then(
                                (m) => m.ReportsPage
                            ),
                    },
                    {
                        path: "features/knowledge-base",
                        canActivate: [paidFeatureGuard("KNOWLEDGE_BASE")],
                        data: {
                            title: "layout.navigation.features.knowledgeBase",
                        },
                        loadComponent: () =>
                            import("./features/knowledge-base/pages/knowledge-base/knowledge-base.page").then(
                                (m) => m.KnowledgeBasePage
                            ),
                    },
                    {
                        path: "settings",
                        data: { title: "dashboard.placeholder.settingsTitle" },
                        loadComponent: () =>
                            import("./features/dashboard/pages/placeholder/placeholder.page").then(
                                (m) => m.PlaceholderPage
                            ),
                    },
                    {
                        path: "settings/profile",
                        data: { title: "layout.navigation.settings.profile" },
                        loadComponent: () =>
                            import("./features/settings/pages/profile-settings/profile-settings.page").then(
                                (m) => m.ProfileSettingsPage
                            ),
                    },
                    {
                        path: "settings/network",
                        data: { title: "settings.network.title" },
                        loadComponent: () =>
                            import("./features/settings/pages/network-compatibility/network-compatibility.page").then(
                                (m) => m.NetworkCompatibilityPage
                            ),
                    },
                    {
                        path: "settings/preferences",
                        canActivate: [clientOwnerAccessGuard],
                        data: {
                            title: "layout.navigation.settings.preferences",
                        },
                        loadComponent: () =>
                            import("./features/settings/pages/preferences/preferences.page").then(
                                (m) => m.PreferencesPage
                            ),
                    },
                    {
                        path: "settings/azure-linking",
                        canActivate: [azureLinkingAccessGuard],
                        data: {
                            title: "layout.navigation.settings.azureLinking",
                        },
                        loadComponent: () =>
                            import("./features/settings/pages/azure-linking/azure-linking.page").then(
                                (m) => m.AzureLinkingPage
                            ),
                    },
                    {
                        path: "settings/plan",
                        data: { title: "layout.navigation.settings.plan" },
                        loadComponent: () =>
                            import("./features/dashboard/pages/placeholder/placeholder.page").then(
                                (m) => m.PlaceholderPage
                            ),
                    },
                    {
                        path: "support",
                        data: { title: "layout.navigation.support.label" },
                        loadComponent: () =>
                            import("./features/dashboard/pages/placeholder/placeholder.page").then(
                                (m) => m.PlaceholderPage
                            ),
                    },
                ],
            },
        ],
    },
    {
        path: "owner",
        canActivate: [authenticatedGuard, roleGuard("owner")],
        data: { navigationProfile: "owner" },
        loadComponent: () =>
            import("./layout/authenticated-layout/authenticated-shell.component").then(
                (m) => m.AuthenticatedShellComponent
            ),
        children: [
            {
                path: "",
                loadComponent: () =>
                    import("./features/owner-portal/pages/owner/owner.page").then(
                        (m) => m.OwnerPage
                    ),
            },
            {
                path: "multiconference/room/:roomId",
                data: { contentLayout: "conference-prejoin" },
                loadComponent: () =>
                    import("./features/multiconference/pages/authenticated-conference/authenticated-conference.page").then(
                        (m) => m.AuthenticatedConferencePage
                    ),
            },
        ],
    },
    {
        path: "partner",
        canActivate: [authenticatedGuard, roleGuard("partner")],
        data: { navigationProfile: "partner" },
        loadComponent: () =>
            import("./layout/authenticated-layout/authenticated-shell.component").then(
                (m) => m.AuthenticatedShellComponent
            ),
        children: [
            {
                path: "",
                loadComponent: () =>
                    import("./features/partner-portal/pages/partner/partner.page").then(
                        (m) => m.PartnerPage
                    ),
            },
        ],
    },
    { path: "**", redirectTo: "login" },
];
